import { useRef, useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

interface QrScannerProps {
  onScan: (value: string) => void;
  onClose: () => void;
  /**
   * When true, the scanner keeps running after each hit — useful for
   * companion-phone batch scan flows where the parent debounces duplicates
   * and wants the camera to stay live. Default: false (one-shot).
   */
  continuous?: boolean;
  /**
   * Default behaviour: on mobile the scanner fixes itself over the entire
   * viewport as a modal camera view. Set embedded to let the parent own
   * the layout — the scanner simply fills its container on every screen
   * size and skips the fullscreen takeover + the built-in close button.
   */
  embedded?: boolean;
}

export function QrScanner({ onScan, onClose, continuous = false, embedded = false }: QrScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);

  // Keep the scan callback in a ref so the camera effect never re-runs
  // when the parent re-creates the closure. Re-running tears down the
  // MediaStream and starts a new RAF loop, which on the web variant
  // (whose parent state churns more than the companion-phone page) read
  // as a constant flicker. The RAF loop reads the freshest onScan via
  // the ref so behavior is identical.
  const onScanRef = useRef(onScan);
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    let animationId: number;
    // Fully detached canvas — never enters the DOM. Some browsers paint
    // mid-frame when a canvas (even display:none) is resized + redrawn on
    // every animation tick, which read as a black flicker on the screen.
    // Keeping the canvas fully offscreen sidesteps that whole class of bug.
    const offscreenCanvas = document.createElement('canvas');

    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch {
        setScanError('Could not access camera. Please allow camera permissions.');
        return;
      }

      // Use native BarcodeDetector if available, otherwise fall back to jsQR.
      if ('BarcodeDetector' in window) {
        const detector = new (window as unknown as { BarcodeDetector: new (opts: { formats: string[] }) => { detect: (source: HTMLVideoElement) => Promise<{ rawValue: string }[]> } }).BarcodeDetector({ formats: ['qr_code'] });

        async function scanNative() {
          if (cancelled || !videoRef.current) return;
          try {
            const barcodes = await detector.detect(videoRef.current);
            if (barcodes.length > 0 && barcodes[0].rawValue) {
              onScanRef.current(barcodes[0].rawValue);
              if (!continuous) return;
            }
          } catch { /* frame not ready yet */ }
          animationId = requestAnimationFrame(scanNative);
        }
        scanNative();
      } else {
        const jsQR = (await import('jsqr')).default;

        function scanCanvas() {
          if (cancelled || !videoRef.current) return;
          const video = videoRef.current;
          if (video.readyState >= video.HAVE_CURRENT_DATA) {
            offscreenCanvas.width = video.videoWidth;
            offscreenCanvas.height = video.videoHeight;
            const ctx = offscreenCanvas.getContext('2d', { willReadFrequently: true });
            if (ctx) {
              ctx.drawImage(video, 0, 0, offscreenCanvas.width, offscreenCanvas.height);
              const imageData = ctx.getImageData(0, 0, offscreenCanvas.width, offscreenCanvas.height);
              const code = jsQR(imageData.data, imageData.width, imageData.height);
              if (code?.data) {
                onScanRef.current(code.data);
                if (!continuous) return;
              }
            }
          }
          animationId = requestAnimationFrame(scanCanvas);
        }
        scanCanvas();
      }
    }

    start();
    return () => {
      cancelled = true;
      cancelAnimationFrame(animationId);
      stopCamera();
    };
  }, [stopCamera, continuous]);

  const containerClass = embedded
    ? 'relative w-full h-full overflow-hidden bg-black'
    : 'relative rounded-lg overflow-hidden bg-black md:aspect-[4/3] max-md:fixed max-md:inset-0 max-md:z-50 max-md:flex max-md:flex-col max-md:rounded-none';
  const videoClass = embedded
    ? 'w-full h-full object-cover'
    : 'w-full object-cover md:aspect-[4/3] max-md:flex-1';

  return (
    <div className={containerClass}>
      <video ref={videoRef} className={videoClass} muted playsInline />
      {!embedded && (
        <button
          onClick={() => { stopCamera(); onClose(); }}
          className="absolute top-2 right-2 p-1.5 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors z-10 max-md:top-4 max-md:right-4 max-md:p-2"
        >
          <X className="w-4 h-4 max-md:w-6 max-md:h-6" />
        </button>
      )}
      {scanError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 p-4">
          <p className="text-sm text-white text-center">{scanError}</p>
        </div>
      )}
      {!scanError && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-48 h-48 border-2 border-white/50 rounded-lg" />
        </div>
      )}
    </div>
  );
}
